import { Feature } from "ol";
import { Extent } from "ol/extent";
import { Point } from "ol/geom";
import { transform } from "ol/proj";
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { fetchApi } from "../../Helpers/Functions";
import { MapContextType } from "../../Types/Map";
import { TripPoint } from "../../Types/Trip";
import { LayersContext } from "../Map/LayerProvider";

export default function RouteTripMap({
  originalCoordsArray,
  children,
}: PropsWithChildren<{
  originalCoordsArray: Array<TripPoint>;
}>) {
  // map
  const mapRef = useRef<HTMLDivElement>(null);
  const { mapObject, markersLayer, routesLayer } = useContext(
    LayersContext
  ) as MapContextType;

  useEffect(() => {
    if (mapRef.current && mapObject) mapObject.setTarget(mapRef.current);
  }, [mapObject]);

  const createMarker = useCallback(
    (markerid: string) => {
      const markerFeature = new Feature({
        geometry: new Point([]),
        name: markerid,
      });
      markersLayer?.getSource()?.addFeature(markerFeature);
      return markerFeature;
    },
    [markersLayer]
  );
  console.log("originalCoordsArray changed", originalCoordsArray);
  useEffect(() => {
    console.log("LEAH");
  }, []);

  useEffect(() => {
    // remove all markers first
    markersLayer
      ?.getSource()
      ?.getFeatures()
      ?.forEach((marker) => {
        markersLayer?.getSource()?.removeFeature(marker);
      });
    // draw markers
    originalCoordsArray.forEach((tripPoint) => {
      if (tripPoint.lat !== 0 && tripPoint.lon !== 0) {
        const markId =
          tripPoint.index === originalCoordsArray.length - 1
            ? "lastPin"
            : tripPoint.address;
        const markerFeature = createMarker(markId);
        const coords = transform(
          [tripPoint.lon, tripPoint.lat],
          "EPSG:4326",
          "EPSG:3857"
        );
        markerFeature?.getGeometry()?.setCoordinates(coords);
        markerFeature?.set("place_id", "movingMarker");
        mapObject?.getView().animate({ zoom: 13 }, { center: coords });
      }
    });
    const ext = markersLayer?.getSource()?.getExtent();
    const feats = markersLayer?.getSource()?.getFeatures();
    if (ext && feats?.length) {
      const paddedExt = addPaddingToExtent(ext, 500);
      mapObject?.getView().fit(paddedExt, { maxZoom: 14 });
    }
  }, [originalCoordsArray]);

  // draw route with points
  useEffect(() => {
    if (routesLayer && originalCoordsArray.length > 1) {
      const origin = originalCoordsArray[0];
      const destination = originalCoordsArray[originalCoordsArray.length - 1];
      const intermediatesPoints = originalCoordsArray.reduce(
        (inters: any, point, i, arr) => {
          if (i === 0 || i === arr.length - 1) return inters;
          if (Math.abs(point.lat) > 0 && Math.abs(point.lon) > 0) {
            const interPoint = {
              location: {
                latLng: {
                  latitude: point.lat,
                  longitude: point.lon,
                },
              },
            };
            inters.push(interPoint);
          }
          return inters;
        },
        []
      );

      if (origin.lon !== 0 && destination.lon !== 0) {
        fetchApi("https://routes.googleapis.com/directions/v2:computeRoutes", {
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": import.meta.env.VITE_GOOGLE_MAPS,
            "X-Goog-FieldMask": "*",
          },
          method: "POST",
          body: JSON.stringify({
            origin: {
              location: {
                latLng: { longitude: origin.lon, latitude: origin.lat },
              },
            },
            destination: {
              location: {
                latLng: {
                  longitude: destination.lon,
                  latitude: destination.lat,
                },
              },
            },
            polylineEncoding: "GEO_JSON_LINESTRING",
            intermediates: [...intermediatesPoints],
          }),
        })
          .then((res) => res.json())
          .then((routeData: any) => {
            console.log("routeData", routeData);
            const routePoints = routeData.routes[0].legs?.map((leg: any) => {
              return leg.polyline.geoJsonLinestring.coordinates?.map(
                (rp: any) => {
                  return transform([rp[0], rp[1]], "EPSG:4326", "EPSG:3857");
                }
              );
            });
            console.log("routePoints", routePoints);
            const flatened = routePoints.flat();
            routesLayer
              ?.getSource()
              ?.getFeatures()
              .find((f) => f.get("name") === "routeLine")
              ?.getGeometry()
              ?.setCoordinates(flatened);
          });
      }
    }
  }, [originalCoordsArray, routesLayer]);

  return (
    <div
      ref={mapRef}
      style={{
        width: "80%",
        minWidth: "300px",
        height: "300px",
      }}
    >
      {children}
    </div>
  );
}

function addPaddingToExtent(extent: Extent, padding: number) {
  const minX = extent[0] - padding;
  const minY = extent[1] - padding;
  const maxX = extent[2] + padding;
  const maxY = extent[3] + padding;

  return [minX, minY, maxX, maxY];
}
